import React from 'react';
import {
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    Box,
    Alert,
    FormHelperText,
    FormControlLabel,
    Checkbox
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { jaJP } from '@mui/x-date-pickers/locales';
import 'dayjs/locale/ja';
import ImageUpload from '../../common/ImageUpload';

function BasicInfoForm({ data, onChange }) {
    const handleChange = (field, value) => {
        onChange({
            ...data,
            profile: {
                ...data.profile,
                [field]: value
            }
        });
    };

    const handleImageUpload = (imageUrl) => {
        handleChange('profileImage', imageUrl);
    };

    return (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 2 }}>
                    ・物件掲載を行う場合も、プロフィール初期設定が必要です。<br />
                    ・虚偽の情報での登録は禁止されています。他のユーザーや本サービスに損害を与えた場合、法的責任を問われる可能性があります。
                </Alert>
            </Grid>

            <Grid item xs={12}>
                <TextField
                    fullWidth
                    required
                    label="ニックネーム"
                    value={data.profile.nickname}
                    onChange={(e) => handleChange('nickname', e.target.value)}
                    inputProps={{
                        minLength: 2,
                        maxLength: 10
                    }}
                    helperText="本名である必要はありません。2文字以上10文字以下で、サイト内で使用する名前を入力してください（※一度設定すると変更できません）"
                />
            </Grid>

            <Grid item xs={12} sm={6}>
                <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale="ja"
                    localeText={jaJP.components.MuiLocalizationProvider.defaultProps.localeText}
                >
                    <DatePicker
                        label="生年月日"
                        value={data.profile.dateOfBirth}
                        onChange={(date) => handleChange('dateOfBirth', date)}
                        disableFuture
                        format="YYYY年M月D日"
                        openTo="year"
                        views={['year', 'month', 'day']}
                        slotProps={{
                            textField: {
                                fullWidth: true,
                                required: true,
                                helperText: "※一度設定すると変更できません"
                            },
                            layout: {
                                sx: {
                                    ['.MuiPickersLayout-root']: {
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 2
                                    },
                                    ['.MuiPickersLayout-contentWrapper']: {
                                        paddingBottom: 2
                                    }
                                }
                            }
                        }}
                    />
                </LocalizationProvider>
            </Grid>

            <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                    <InputLabel>性別</InputLabel>
                    <Select
                        value={data.profile.gender}
                        onChange={(e) => handleChange('gender', e.target.value)}
                        label="性別"
                    >
                        <MenuItem value="male">男性</MenuItem>
                        <MenuItem value="female">女性</MenuItem>
                    </Select>
                    <FormHelperText>※一度設定すると変更できません</FormHelperText>
                </FormControl>
            </Grid>

            <Grid item xs={12}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={data.profile.sameGenderOnly || false}
                            onChange={(e) => handleChange('sameGenderOnly', e.target.checked)}
                        />
                    }
                    label="同性からの連絡のみ受け付ける"
                />
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                    チェックすると、異性からのメッセージ・お問い合わせをすべてブロックします。
                    あなたの物件や募集は異性のユーザーには「連絡不可」と表示されます。
                    安全にルームシェアパートナーを探したい方におすすめです。
                </Typography>
            </Grid>

            <Grid item xs={12}>
                <TextField
                    fullWidth
                    label="職業"
                    value={data.profile.occupation}
                    onChange={(e) => handleChange('occupation', e.target.value)}
                    inputProps={{ maxLength: 20 }}
                    helperText="20文字以下で入力してください"
                />
            </Grid>

            <Grid item xs={12}>
                <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="自己紹介"
                    value={data.profile.introduction}
                    onChange={(e) => handleChange('introduction', e.target.value)}
                    inputProps={{ maxLength: 1000 }}
                    helperText="できるだけ詳細に記載すると格段にルームシェア相手が探しやすくなります（本人が特定できるような個人情報や連絡先は記載しないでください）"
                />
                <Box sx={{ mt: 1, backgroundColor: 'grey.100', p: 2, borderRadius: 1 }}>
                    <Typography variant="caption" color="textSecondary">
                        【記載例】<br />
                        ・生活リズム：平日は9-18時で仕事、休日は主に家で過ごします<br />
                        ・趣味：料理、映画鑑賞、ジム通い<br />
                        ・性格：マイペースですが、ルールはしっかり守ります<br />
                        ・希望する相手：お互いのプライバシーを尊重できる方<br />
                        ・生活スタイル：整理整頓を心がけています。共用スペースは使ったらすぐ片付けます<br />
                        ・コミュニケーション：必要最小限で大丈夫ですが、たまには一緒に食事もしたいです
                    </Typography>
                </Box>
            </Grid>

            <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                    プロフィール画像
                </Typography>
                <Box sx={{ mt: 1 }}>
                    <ImageUpload
                        onFileSelect={handleImageUpload}
                        value={data.profile.profileImage}
                        variant="profile"
                        uploadPath="profiles"
                        title="プロフィール画像"
                        maxSize={5242880}
                    />
                </Box>
                <Typography variant="caption" color="textSecondary">
                    雰囲気がわかる本人画像にするとルームシェア相手が格段に探しやすくなります
                </Typography>
            </Grid>
        </Grid>
    );
}

export default BasicInfoForm; 